/**
 * Settings → Integrations → FOCTTA Privacy Core
 *
 * The browser-driven "Connect to FOCTTA Core" wizard.
 *
 * Flow:
 *   1. Status card - current connection state
 *   2. If disconnected: 4-step wizard collects baseUrl + tenantId + 3 secrets
 *   3. Probe runs 5 checks live (tenant features, RoPA, BUs, consent, audit egress)
 *   4. On success: status flips to "Connected" + SDK starts hitting core live
 *   5. From connected state: "Re-test" or "Disconnect" actions
 *
 * No SSH. No env files. No restart. All in the browser.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Link, Unlink, CheckCircle2, XCircle, Loader2, ShieldCheck, AlertTriangle, KeyRound, Settings2 } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  getFocttaCoreStatus,
  connectFocttaCore,
  connectFocttaCoreByToken,
  testFocttaCore,
  disconnectFocttaCore,
  type FocttaCoreIntegrationStatus,
  type ConnectFocttaCoreResult,
  type ConnectByTokenResult,
} from '@/lib/api';

export function AdminIntegrationsFocttaCore(): JSX.Element {
  const [status, setStatus] = useState<FocttaCoreIntegrationStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [probe, setProbe] = useState<ConnectFocttaCoreResult | ConnectByTokenResult | null>(null);
  const [testResult, setTestResult] = useState<{ checks: Array<{ check: string; ok: boolean; detail?: string }>; allOk: boolean } | null>(null);
  // Primary path: single enrollment-token paste (BFSI-grade, v1)
  const [enrollmentToken, setEnrollmentToken] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);

  // Legacy 7-field form (fallback while FOCTTA core operators upgrade to JWT-issuing wizard)
  const [baseUrl, setBaseUrl] = useState('https://app.foctta.com');
  const [remoteTenantId, setRemoteTenantId] = useState('00000000-0000-4000-9100-000000000001');
  const [apiKey, setApiKey] = useState('');
  const [egressSecret, setEgressSecret] = useState('');
  const [scimToken, setScimToken] = useState('');
  const [inboundSecret, setInboundSecret] = useState('');
  const [displayName, setDisplayName] = useState('FOCTTA Core (production)');

  async function refresh(): Promise<void> {
    try {
      const s = await getFocttaCoreStatus();
      setStatus(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to read status');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleConnect(): Promise<void> {
    setError(null);
    setProbe(null);
    setBusy(true);
    try {
      const result = await connectFocttaCore({
        displayName,
        baseUrl,
        remoteTenantId,
        apiKey,
        hmacEgressSecret: egressSecret,
        scimToken: scimToken || undefined,
        inboundWebhookSecret: inboundSecret,
        featureFlags: {
          consent_enrichment: true,
          ingest_dspm_assets: true,
          business_unit_sync: true,
          egress_audit: true,
        },
      });
      setProbe(result);
      await refresh();
      setWizardOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'connect failed');
    } finally {
      setBusy(false);
    }
  }

  /**
   * BFSI-grade single-paste Connect.
   *
   * The operator pastes one signed JWT (the "enrollment token") that
   * FOCTTA core's install wizard generated. Discovery verifies the
   * signature against FOCTTA core's JWKS, extracts all 5 secrets +
   * scopes + event subscriptions, and runs the same 5-probe handshake.
   *
   * No fields to type. No secrets to copy 5 times. One paste, one click.
   *
   * Spec: docs/integration-spec/FOCTTA-ENROLLMENT-TOKEN-V1.md
   */
  async function handleConnectByToken(): Promise<void> {
    setError(null);
    setProbe(null);
    setBusy(true);
    try {
      const result = await connectFocttaCoreByToken({
        enrollmentToken: enrollmentToken.trim(),
        discoveryBaseUrl: window.location.origin,
      });
      setProbe(result);
      await refresh();
      setEnrollmentToken('');
      setWizardOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'connect failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleTest(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const r = await testFocttaCore();
      setTestResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'test failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(): Promise<void> {
    if (!window.confirm('Disconnect from FOCTTA Core? Discovery will revert to standalone mode (placeholder ledgers).')) return;
    setError(null);
    setBusy(true);
    try {
      await disconnectFocttaCore('Tenant admin disconnected via Settings UI');
      setProbe(null);
      setTestResult(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'disconnect failed');
    } finally {
      setBusy(false);
    }
  }

  const connected = status?.connected === true;

  return (
    <PageContainer>
      <PageHeader
        title="Integrations · FOCTTA Privacy Core"
        subtitle="Connect Discovery to your FOCTTA core (consent + DSAR + RoPA + breach) for end-to-end privacy + security in one dashboard. When connected, Discovery's consent firewall, Section 9 guardrail, reverse-DSAR, audit chain, and breach console all read from FOCTTA core's authoritative data."
        badges={[
          { label: connected ? 'Connected' : 'Standalone', variant: connected ? 'success' : 'glass' },
          { label: 'browser-driven setup', variant: 'info' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      {connected ? (
        <ConnectedStatusCard
          status={status!}
          busy={busy}
          onTest={handleTest}
          onDisconnect={handleDisconnect}
          testResult={testResult}
        />
      ) : (
        <DisconnectedCard onOpenWizard={() => setWizardOpen(true)} />
      )}

      {probe && (
        <GlassCard className="p-5 mt-4 border-primary glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Connected - probe results</CardTitle>
            <CardDescription>{probe.message}</CardDescription>
          </CardHeader>
          <ProbeChecks checks={probe.probe} />
        </GlassCard>
      )}

      {wizardOpen && !showManualForm && (
        <ConnectByTokenWizard
          enrollmentToken={enrollmentToken}
          setEnrollmentToken={setEnrollmentToken}
          busy={busy}
          onConnect={handleConnectByToken}
          onCancel={() => {
            setWizardOpen(false);
            setEnrollmentToken('');
          }}
          onUseManual={() => setShowManualForm(true)}
        />
      )}

      {wizardOpen && showManualForm && (
        <ConnectWizard
          displayName={displayName}
          setDisplayName={setDisplayName}
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          remoteTenantId={remoteTenantId}
          setRemoteTenantId={setRemoteTenantId}
          apiKey={apiKey}
          setApiKey={setApiKey}
          egressSecret={egressSecret}
          setEgressSecret={setEgressSecret}
          scimToken={scimToken}
          setScimToken={setScimToken}
          inboundSecret={inboundSecret}
          setInboundSecret={setInboundSecret}
          busy={busy}
          onConnect={handleConnect}
          onCancel={() => {
            setWizardOpen(false);
            setShowManualForm(false);
          }}
          onBackToToken={() => setShowManualForm(false)}
        />
      )}
    </PageContainer>
  );
}

function ConnectedStatusCard({
  status, busy, onTest, onDisconnect, testResult,
}: {
  status: FocttaCoreIntegrationStatus;
  busy: boolean;
  onTest: () => void;
  onDisconnect: () => void;
  testResult: { checks: Array<{ check: string; ok: boolean; detail?: string }>; allOk: boolean } | null;
}): JSX.Element {
  return (
    <GlassCard className="p-6 mb-4">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-success" />
          {status.displayName ?? 'FOCTTA Core'} · connected
        </CardTitle>
        <CardDescription>
          {status.baseUrl} · tenant <code className="font-mono text-[11px]">{status.remoteTenantId}</code>
        </CardDescription>
      </CardHeader>

      {/* Top stats row - 3 columns + dedicated full-width event chips block below */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div className="glass-lite p-3 rounded-md">
          <div className="text-[9px] uppercase text-muted-foreground">Status</div>
          <div className="text-sm font-medium">{status.status}</div>
        </div>
        <div className="glass-lite p-3 rounded-md">
          <div className="text-[9px] uppercase text-muted-foreground">Last success</div>
          <div className="text-xs">{status.lastSuccessAt ? new Date(status.lastSuccessAt).toLocaleString() : '-'}</div>
        </div>
        <div className="glass-lite p-3 rounded-md">
          <div className="text-[9px] uppercase text-muted-foreground">Consec failures</div>
          <div className="text-sm">{status.consecutiveFailures ?? 0}</div>
        </div>
      </div>

      {/* Subscribed events - chip-rendered + wrapped so we don't overflow.
          When the wildcard '*' is subscribed, render a single emphatic chip. */}
      {(() => {
        const events = status.subscribedEventTypes ?? '*';
        const eventList =
          events === '*' ? null : events.split(',').map((e) => e.trim()).filter(Boolean);
        return (
          <div className="glass-lite p-3 rounded-md mb-4">
            <div className="text-[9px] uppercase text-muted-foreground mb-1.5">
              Subscribed events ({eventList === null ? 'all' : eventList.length})
            </div>
            {eventList === null ? (
              <Badge variant="info" className="text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-0.5 inline" />
                wildcard · all events
              </Badge>
            ) : (
              <div className="flex flex-wrap gap-1">
                {eventList.map((evt) => (
                  <Badge key={evt} variant="glass" className="text-[10px] font-mono">
                    {evt}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {status.featureFlags && (
        <div className="mb-4">
          <div className="text-[10px] uppercase text-muted-foreground mb-2">Active wires</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(status.featureFlags).map(([k, v]) => (
              <Badge key={k} variant={v ? 'success' : 'glass'} className="text-[10px]">
                {v ? <CheckCircle2 className="h-3 w-3 mr-0.5 inline" /> : <XCircle className="h-3 w-3 mr-0.5 inline" />}
                {k.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onTest} disabled={busy} variant="glass" size="sm">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Re-test
        </Button>
        <Button onClick={onDisconnect} disabled={busy} variant="destructive" size="sm">
          <Unlink className="h-3 w-3" /> Disconnect
        </Button>
      </div>

      {testResult && (
        <div className="mt-4 pt-4 border-t border-border/40">
          <div className="text-[10px] uppercase text-muted-foreground mb-2">Last test result · {testResult.allOk ? 'all green' : 'some failed'}</div>
          <ProbeChecks checks={testResult.checks} />
        </div>
      )}
    </GlassCard>
  );
}

function DisconnectedCard({ onOpenWizard }: { onOpenWizard: () => void }): JSX.Element {
  return (
    <GlassCard className="p-6 mb-4">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          Not connected · running in standalone mode
        </CardTitle>
        <CardDescription>
          Discovery currently uses placeholder ledgers for consent, RoPA, and business units. Connecting to your FOCTTA Core privacy app unlocks live data across:
        </CardDescription>
      </CardHeader>
      <ul className="text-xs text-muted-foreground space-y-1 mb-4 list-disc pl-5">
        <li>Consent-Aware Query Firewall (lookups against your real consent records)</li>
        <li>Section 9 minor-data guardrail (verifies guardian-consent receipts)</li>
        <li>Cross-border geofence (reads your SCC/BCR registry + vendor DPAs)</li>
        <li>Subject data discovery (joins existing DSAR cases + your RoPA)</li>
        <li>UEBA actor identity (replaces "unknown role" with real names + departments)</li>
        <li>Audit chain (single per-tenant SHA-256 chain across privacy + Discovery)</li>
        <li>Breach console pre-fill (writes incidents to FOCTTA's authoritative table)</li>
      </ul>
      <Button onClick={onOpenWizard} size="lg">
        <Link className="h-4 w-4" /> Connect to FOCTTA Core
      </Button>
    </GlassCard>
  );
}

function ProbeChecks({ checks }: { checks: Array<{ check: string; ok: boolean; detail?: string }> }): JSX.Element {
  return (
    <div className="space-y-1.5">
      {checks.map((c, i) => (
        <motion.div
          key={c.check}
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.05 }}
          className="flex items-center gap-2 glass-lite p-2 rounded-md text-xs"
        >
          {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
          <code className="font-mono">{c.check}</code>
          {c.detail && <span className="text-muted-foreground text-[10px]">- {c.detail}</span>}
        </motion.div>
      ))}
    </div>
  );
}

/**
 * BFSI-grade primary Connect wizard - single-paste enrollment token.
 *
 * Spec: docs/integration-spec/FOCTTA-ENROLLMENT-TOKEN-V1.md
 */
function ConnectByTokenWizard(props: {
  enrollmentToken: string;
  setEnrollmentToken: (v: string) => void;
  busy: boolean;
  onConnect: () => void;
  onCancel: () => void;
  onUseManual: () => void;
}): JSX.Element {
  const trimmed = props.enrollmentToken.trim();
  const looksLikeJWT = /^eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(trimmed);
  const canSubmit = looksLikeJWT && !props.busy;

  // Peek at the token (no signature verify; just for UX preview) to
  // show the operator what they're about to authorise.
  let preview: { iss?: string; aud?: string; name?: string; sc?: string[]; exp?: number } | null = null;
  if (looksLikeJWT) {
    try {
      const parts = trimmed.split('.');
      preview = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      preview = null;
    }
  }

  return (
    <GlassCard className="p-6 mt-4 border-primary">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          Paste enrollment token
        </CardTitle>
        <CardDescription>
          The single signed token FOCTTA core's install wizard generated. Discovery verifies the
          signature against FOCTTA core's public key, extracts all 5 secrets + scopes + event
          subscriptions, and runs 5 live probes. <strong>One paste, one click, BFSI-grade.</strong>
        </CardDescription>
      </CardHeader>

      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
            FOCTTA enrollment token (RS256 JWT)
          </label>
          <textarea
            value={props.enrollmentToken}
            onChange={(e) => props.setEnrollmentToken(e.target.value)}
            placeholder="eyJhbGciOiJSUzI1NiIs..."
            rows={6}
            className="w-full glass-lite rounded-md p-2 text-[10px] font-mono outline-none resize-none break-all"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Issued by your FOCTTA core operator at <code>{'<core-url>/integrations/foctta-discovery'}</code>{' '}
            → Done step. Expires 10 minutes after issue. Single-use (replay-protected).
          </p>
        </div>

        {preview && (
          <div className="glass-lite rounded-md p-3 space-y-1.5">
            <div className="text-[10px] uppercase text-muted-foreground">Token preview</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              <div>
                <span className="text-muted-foreground">Issuer:</span>{' '}
                <code className="font-mono">{preview.iss}</code>
              </div>
              <div>
                <span className="text-muted-foreground">Audience:</span>{' '}
                <code className="font-mono">{preview.aud}</code>
              </div>
              <div>
                <span className="text-muted-foreground">Display name:</span>{' '}
                {preview.name}
              </div>
              <div>
                <span className="text-muted-foreground">Expires:</span>{' '}
                {preview.exp ? new Date(preview.exp * 1000).toLocaleString() : '-'}
              </div>
            </div>
            {preview.sc && preview.sc.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] text-muted-foreground mb-1">
                  Discovery will be granted {preview.sc.length} scopes:
                </div>
                <div className="flex flex-wrap gap-1">
                  {preview.sc.map((s) => (
                    <Badge key={s} variant="glass" className="text-[9px] font-mono">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {props.enrollmentToken.length > 0 && !looksLikeJWT && (
          <div className="text-[11px] text-warning flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            That doesn't look like a JWT. Expected format:{' '}
            <code className="font-mono">eyJxxx.eyJxxx.xxx</code>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4 flex-wrap">
        <Button onClick={props.onConnect} disabled={!canSubmit}>
          {props.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link className="h-3 w-3" />}{' '}
          Connect + verify
        </Button>
        <Button onClick={props.onCancel} variant="glass" disabled={props.busy}>
          Cancel
        </Button>
        <div className="flex-1" />
        <Button
          onClick={props.onUseManual}
          variant="glass"
          size="sm"
          disabled={props.busy}
          title="Use the legacy 7-field form if your FOCTTA core operator's install wizard doesn't yet issue enrollment tokens (pre-v1)"
        >
          <Settings2 className="h-3 w-3" /> Use manual entry (legacy)
        </Button>
      </div>
    </GlassCard>
  );
}

function ConnectWizard(props: {
  displayName: string; setDisplayName: (v: string) => void;
  baseUrl: string; setBaseUrl: (v: string) => void;
  remoteTenantId: string; setRemoteTenantId: (v: string) => void;
  apiKey: string; setApiKey: (v: string) => void;
  egressSecret: string; setEgressSecret: (v: string) => void;
  scimToken: string; setScimToken: (v: string) => void;
  inboundSecret: string; setInboundSecret: (v: string) => void;
  busy: boolean;
  onConnect: () => void;
  onCancel: () => void;
  onBackToToken?: () => void;
}): JSX.Element {
  const canSubmit = props.baseUrl && props.remoteTenantId && props.apiKey && props.egressSecret && props.inboundSecret;
  return (
    <GlassCard className="p-6 mt-4">
      <CardHeader className="p-0 mb-3">
        <CardTitle>Connect to FOCTTA Privacy Core</CardTitle>
        <CardDescription>
          Paste the credentials FOCTTA core generated when an admin clicked Install FOCTTA Discovery in the Integrations marketplace. The wizard runs 5 live checks before saving.
        </CardDescription>
      </CardHeader>
      <div className="space-y-3">
        <Field label="Display name" value={props.displayName} onChange={props.setDisplayName} />
        <Field label="FOCTTA core URL" value={props.baseUrl} onChange={props.setBaseUrl} placeholder="https://app.foctta.com" />
        <Field
          label="Remote tenant id (UUID - your FOCTTA-side tenant)"
          value={props.remoteTenantId}
          onChange={props.setRemoteTenantId}
          placeholder="00000000-0000-4000-9100-000000000001"
        />
        <Field label="Service-principal API key (keyType=service)" value={props.apiKey} onChange={props.setApiKey} secret />
        <Field label="HMAC egress secret (for audit chain push)" value={props.egressSecret} onChange={props.setEgressSecret} secret />
        <Field label="Inbound webhook secret (FOCTTA core signs deliveries with this)" value={props.inboundSecret} onChange={props.setInboundSecret} secret />
        <Field label="SCIM bearer token (optional - enables user-directory enrichment)" value={props.scimToken} onChange={props.setScimToken} secret />
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        <Button onClick={props.onConnect} disabled={!canSubmit || props.busy}>
          {props.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link className="h-3 w-3" />} Connect + run 5 probes
        </Button>
        <Button onClick={props.onCancel} variant="glass" disabled={props.busy}>Cancel</Button>
        {props.onBackToToken && (
          <Button onClick={props.onBackToToken} variant="glass" size="sm" disabled={props.busy}>
            <KeyRound className="h-3 w-3" /> Back to enrollment token (BFSI-grade)
          </Button>
        )}
      </div>
    </GlassCard>
  );
}

function Field({ label, value, onChange, placeholder, secret }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; secret?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">{label}</label>
      <input
        type={secret ? 'password' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full glass-lite rounded-md p-2 text-xs font-mono outline-none"
        autoComplete="off"
      />
    </div>
  );
}
